import { Link } from '@/components/ui/link';
import { ThemeControl } from '@/donor/theme';
import { DonorNavigation } from '@/donor/navigation';
import { useLocation } from 'react-router-dom';
import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { useLanguage } from '@/i18n/language-context';
import { env } from '@/config/env';
import { APP_VERSION } from '@/version';
import { enableDeveloperFeatures, useDeveloperFeatures } from '@/lib/developer-features';

import { Brand } from './brand';
import { AppStoreBadges } from './app-store-badges';
import { LanguageSwitcher } from './language-switcher';
import { LanguageSyncNotice } from './language-sync-notice';

export const SiteShell = ({
  children,
  mobileAction,
}: {
  children: ReactNode;
  mobileAction?: ReactNode;
}): JSX.Element => {
  const { t } = useLanguage();
  const { donorNavigation } = useDeveloperFeatures();
  const { pathname } = useLocation();
  const mobileNavigation =
    donorNavigation && !pathname.startsWith('/p/') && !pathname.startsWith('/checkout/');
  const versionPresses = useRef(0);
  const shell = useRef<HTMLDivElement>(null);
  const mobileBar = useRef<HTMLDivElement>(null);
  const hasMobileAction = Boolean(mobileAction);
  useEffect(() => {
    const bar = mobileBar.current;
    const container = shell.current;
    if (!bar || !container) return;
    const measure = (): void => {
      container.style.setProperty(
        '--mobile-action-height',
        `${bar.getBoundingClientRect().height}px`,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    const viewport = window.visualViewport;
    const followKeyboard = (): void => {
      const editing = bar.contains(document.activeElement);
      const offset =
        viewport && editing && viewport.scale === 1
          ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
          : 0;
      bar.style.setProperty('--mobile-keyboard-offset', `${offset}px`);
      bar.style.setProperty(
        '--mobile-viewport-height',
        `${viewport?.height ?? window.innerHeight}px`,
      );
    };
    followKeyboard();
    viewport?.addEventListener('resize', followKeyboard);
    viewport?.addEventListener('scroll', followKeyboard);
    bar.addEventListener('focusin', followKeyboard);
    bar.addEventListener('focusout', followKeyboard);
    return () => {
      observer.disconnect();
      viewport?.removeEventListener('resize', followKeyboard);
      viewport?.removeEventListener('scroll', followKeyboard);
      bar.removeEventListener('focusin', followKeyboard);
      bar.removeEventListener('focusout', followKeyboard);
    };
  }, [hasMobileAction]);
  return (
    <div
      ref={shell}
      className={`site-shell${pathname === '/basket' ? ' site-shell--basket' : ''}${mobileAction ? ' site-shell--mobile-action' : mobileNavigation ? ' site-shell--mobile-navigation' : ''}`}
    >
      <a className="skip-link" href="#main-content">
        {t.skipContent}
      </a>
      <header className="site-header">
        <div className="site-header__inner">
          <Brand />
          <div className="giving-header-actions">
            {donorNavigation && <DonorNavigation />}
            <div className="giving-header-preferences">
              <ThemeControl />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>
      <LanguageSyncNotice />
      {children}
      <footer className="site-footer">
        <div
          className={`site-footer__main${env.legalSiteUrl === null ? ' site-footer__main--compact' : ''}`}
        >
          <div className="site-footer__brand">
            <div className="site-footer__identity">
              <Brand />
              <Link
                appearance="bare"
                className="site-footer__union-link"
                href="https://adventist.ro/"
                title={t.unionName}
                target="_blank"
                rel="noopener noreferrer"
              >
                adventist.ro <ArrowUpRight size={13} aria-hidden="true" />
              </Link>
            </div>
            <AppStoreBadges />
          </div>
          {env.legalSiteUrl !== null && (
            <nav className="site-footer__group" aria-labelledby="footer-legal-title">
              <h2 id="footer-legal-title">{t.footerLegal}</h2>
              <Link appearance="bare" href={`${env.legalSiteUrl}/terms`} rel="noreferrer">
                {t.terms}
              </Link>
              <Link appearance="bare" href={`${env.legalSiteUrl}/imprint`} rel="noreferrer">
                {t.imprint}
              </Link>
            </nav>
          )}
          <nav className="site-footer__group" aria-labelledby="footer-data-title">
            <h2 id="footer-data-title">{t.footerYourData}</h2>
            <Link appearance="bare" to="/privacy">
              {t.footerPrivacy}
            </Link>
            <Link appearance="bare" to="/delete-account">
              {t.accountDeletion}
            </Link>
          </nav>
        </div>
        <div className="site-footer__bottom">
          <small>
            © {new Date().getFullYear()} {t.appName}
          </small>
          <small
            className="site-footer__version"
            data-testid="app-version"
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
                event.preventDefault();
                versionPresses.current += 1;
                if (versionPresses.current === 10) enableDeveloperFeatures();
              }
            }}
            onClick={() => {
              versionPresses.current += 1;
              if (versionPresses.current === 10) enableDeveloperFeatures();
            }}
          >
            {t.version} {APP_VERSION}
          </small>
        </div>
      </footer>
      {mobileAction && (
        <div ref={mobileBar} className="giving-mobile-action">
          {mobileAction}
        </div>
      )}
    </div>
  );
};
